import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { BrManageContentButton } from '@bloomreach/react-sdk'
import FundCard from './FundCard.jsx'
import {
  getLocalPath,
  isMobile,
  isScreenSmall,
  isScreenMedium,
  isScreenBig,
} from '../../utility'
import FundCardIntro from './FundCardIntro.jsx'
import ScrollContainer from './ScrollContainer.jsx'
import { getDocumentFromPageModel, halApi } from '../utilities/utilities.js'
import DOMPurify from 'dompurify'

function getScreenSize() {
  const { name } = [isMobile, isScreenSmall, isScreenMedium, isScreenBig].find(
    (el) => el()
  )
  return name
}
export const FundCards = ({ component, page }) => {
  const [cards, setCards] = useState([])
  const [sectionIntro, setSectionIntro] = useState(null)
  const [screenSize, setScreenSize] = useState(getScreenSize())
  const [minHeight, setMinHieght] = useState(0)
  const params = component.getParameters()
  // for document selected inside of expereince manager's document picker
  // there can only be one selected per component, in this case it's the
  // find card's intro section
  const _documentRef = page.getContent(component?.getModels()?.document)

  const minHeightRef = useRef([])
  const scope = params?.scope
  const backgroundColor = params?.backgroundColor
  const backgroundColorCss =
    backgroundColor == 'gray'
      ? 'u-decoBackgroundNeutral05'
      : 'u-decoBackgroundNeutralWhite'

  const doctypeFilter = (type) => {
    return (document) => {
      return document._meta.type === type
    }
  }

  const featureFundFilter = doctypeFilter('cti:FeatureFund')
  useEffect(() => {
    const getDocumentsInFolder = async (scope) => {
      const response = await halApi('documents', scope)

      const featureFundDocs =
        response._embedded.documents.filter(featureFundFilter)
      const featureFundCards = featureFundDocs.map((document) => {
        return {
          documentFromPageApi: page.getContent(
            `u${document._meta.id.replaceAll('-', '')}`
          ),
          fundName: document.fundName,
          ticker: document.ticker,
          category: document.category,
          body: document.body,
          rating: document.rating,
          cta: document.cta,
          highlightColor: document.highlightColor,
          assetClass: document.assetClass,
          footerTitle: document.footerTitle,
          footerText: document.footerText,
          footerContentCollapsed: document.footerContentCollapsed,
        }
      })
      setCards(featureFundCards)
    }
    getDocumentsInFolder(scope)
  }, [])
  useEffect(() => {
    if (params.document === '') {
      setSectionIntro(null)
      return
    }
    if (params.document.startsWith('/page/')) {
      const intro = getDocumentFromPageModel(page, params.document)
      setSectionIntro(intro)
      return
    }
    const getDocument = async (scope) => {
      const response = await halApi('documents', scope)
      const intro = response._meta.size === 1 ? response : null
      setSectionIntro(intro)
    }
    getDocument(params.document)
  }, [params.document])

  useEffect(() => {
    minHeightRef.current = minHeightRef.current.slice(0, cards.length)

    const max = minHeightRef.current.reduce((maxHeight, currentRef) => {
      const rect = currentRef?.getBoundingClientRect()
      return Math.max(rect.height, maxHeight)
    }, 0)

    setMinHieght(max)
  }, [cards])

  useEffect(() => {
    //reset minHieght based on if the screen changes
    const handleResize = () => {
      const foundScreen = getScreenSize()
      if (foundScreen !== screenSize) {
        setScreenSize(foundScreen)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  let layout = sectionIntro ? 'withIntro' : 'defaultLayout'
  if (params?.singleRow) {
    layout += 'SingleRow'
  }

  const layouts = {
    defaultLayout: (cards) => {
      return (
        <div className="Grid Grid--withGutter FundCards">
          {getCardsWithGridSize(
            cards,
            'u-size1of4 u-md-size1of3 u-sm-sizeFull u-flex u-flexWrap',
            minHeightRef
          )}
        </div>
      )
    },
    defaultLayoutSingleRow: (cards) => {
      return (
        <ScrollContainer
          cards={getCardsWithGridSize(
            cards,
            'u-size1of4 u-md-size1of3 u-sm-sizeFull u-flex scroll-card',
            minHeightRef
          )}
          backgroundColor={backgroundColor}
        />
      )
    },
    withIntro: (cards, sectionIntro) => {
      return (
        <div className="Grid Grid--withGutter FundCards">
          <div className="u-size1of4 u-sm-sizeFull u-marginBottom4gu">
            {FundCardIntro(sectionIntro, page)}
          </div>
          <div className="u-size3of4 u-sm-sizeFull Grid Grid--withGutter u-flexGrow1 u-flexBasisAuto">
            {getCardsWithGridSize(
              cards,
              'u-size1of3 u-md-size1of2 u-sm-sizeFull u-flex',
              minHeightRef
            )}
          </div>
        </div>
      )
    },
    withIntroSingleRow: (cards, sectionIntro) => {
      return (
        <ScrollContainer
          intro={FundCardIntro(sectionIntro, page)}
          cards={getCardsWithGridSize(
            cards,
            'u-size1of3 u-md-size1of2 u-sm-sizeFull u-flex u-posRelative scroll-card'
          )}
          backgroundColor={backgroundColor}
        />
      )
    },
  }

  const getCardsWithGridSize = (cards, gridSize, minHeightRef) => {
    return cards.map((el, index) => {
      return (
        <div
          className={`${gridSize} u-marginBottom4gu u-posRelative`}
          key={index}
        >
          <FundCard
            card={el}
            minHeight={minHeight}
            ref={(el) => (minHeightRef.current[index] = el)}
          >
            <BrManageContentButton content={el.documentFromPageApi} />
          </FundCard>
        </div>
      )
    })
  }

  const classes = `fund-card-block u-sizeConstrained u-padding5gu${
    !cards ? ' u-flex u-flexJustifyCenter' : ''
  }`

  return (
    <div data-interaction-studio="IS015">
      <div
        data-analytics="fund-card"
        className={`fund-card-wrapper ${backgroundColorCss} u-posRelative ${
          page?.isPreview() ? 'has-edit-button' : ''
        }`}
      >
        {<BrManageContentButton content={_documentRef} />}

        <div className={classes}>
          {cards ? (
            layouts[layout](cards, sectionIntro)
          ) : (
            <img
              src={DOMPurify.sanitize(
                getLocalPath() +
                  `/webfiles/latest/assets/cti/images/cti-page-loader-icon.gif`
              )}
              alt="Loading..."
            />
          )}
        </div>
      </div>
    </div>
  )
}

FundCards.propTypes = {
  page: PropTypes.object,
  component: PropTypes.object,
  children: PropTypes.array,
}
